import { Box, Flex, Grid, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { queryParams } from 'utils/query';
import { DebouncedInput } from 'components/ControlledInputs/DebouncedInput';
import { MoreFiltersDrawer } from 'components/Table/Filters/MoreFiltersDrawer';
import { ActiveFiltersBar } from 'components/Table/Filters/ActiveFiltersBar';
import { TableConfig } from 'models/general';
import { PatientHasMeasurement, PatientStatus } from 'utils/models';
import { IndicatorLabel } from 'components/IndicatorLabel';
import { InfoIcon } from 'components/InfoIcon';
import { PortalButton } from 'components/Button/PortalButton';
import { defaultMeasurementIndicators, getHeartRhythmIndicatorLabel } from 'utils/measurement';
import { FilterGroups } from './FilterGroups';
import { FilterDates } from './FilterDates';
import { FilterBirthday } from './FilterBirthday';
import { FilterOptionList } from './FilterOptionList';
import { FilterCheckList } from './FilterCheckList';
import { useBadges } from './PatientsListBadges';

interface Props {
  config: TableConfig;
}

export const PatientsListFilters = ({ config }: Props) => {
  const { queryKey } = config;
  const { t } = useTranslation();
  const { parameters: filters, setParameters, resetParameters } = useSearchParameters(queryKey);

  const handleFilterChange = (filterKey: string, input: string) => {
    setParameters({ ...filters, [filterKey]: input });
  };

  const indicatorChoices = defaultMeasurementIndicators.map((value) => ({
    value,
    label: <IndicatorLabel indicator={value} label={getHeartRhythmIndicatorLabel(value)} />,
  }));

  const statusChoices = Object.values(PatientStatus).map((status) => ({
    value: status,
    label: t(`label_patient_status_${status.toLowerCase()}`),
  }));

  const hasMeasurementChoices = Object.values(PatientHasMeasurement).map((hasMeasurement) => ({
    value: hasMeasurement,
    label: t(`label_patient_has_measurement_${hasMeasurement}`),
  }));

  return (
    <Box p={'xs'}>
      <Flex align={'center'} justify={'space-between'} gap={'xs'}>
        <Grid align={'center'} style={{ flex: 1 }}>
          <Grid.Col span={2}>
            <DebouncedInput
              searchOption
              onChange={(value) => handleFilterChange(queryParams.lastname, value)}
              value={filters[queryParams.lastname]}
              placeholder={t('search_by_lastname')}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <DebouncedInput
              searchOption
              onChange={(value) => handleFilterChange(queryParams.firstname, value)}
              value={filters[queryParams.firstname]}
              placeholder={t('search_by_firstname')}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <DebouncedInput
              searchOption
              onChange={(value) => handleFilterChange(queryParams.patientId, value)}
              value={filters[queryParams.patientId]}
              placeholder={t('patients_group_id_label')}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <FilterBirthday queryKey={queryKey} />
          </Grid.Col>
          <Grid.Col span={2.75}>
            <FilterGroups queryKey={queryKey} />
          </Grid.Col>
          <Grid.Col span={'content'}>
            <MoreFiltersDrawer queryKey={queryKey}>
              <Grid>
                <Grid.Col span={6}>
                  <FilterOptionList
                    config={config}
                    filterField={queryParams.status}
                    title={t('label_patient_status')}
                    choices={statusChoices}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <FilterOptionList
                    config={config}
                    filterField={queryParams.hasMeasurement}
                    title={t('label_patient_has_measurement')}
                    choices={hasMeasurementChoices}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <FilterCheckList
                    filterField={queryParams.lastMeasurementResultType}
                    title={t('filter_last_measurement_result')}
                    queryKey={queryKey}
                    choices={indicatorChoices}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <FilterCheckList
                    filterField={queryParams.lastMostSevereMeasurementResultType}
                    title={t('filter_last_most_severe_measurement_result')}
                    queryKey={queryKey}
                    choices={indicatorChoices}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <FilterDates title={t('filter_last_measurement_date')} queryKey={queryKey} />
                </Grid.Col>
              </Grid>
            </MoreFiltersDrawer>
          </Grid.Col>
        </Grid>
        <Group align={'center'} wrap={'nowrap'} gap={'xs'}>
          <PortalButton onClick={resetParameters} label={t('reset_filters')} variant={'outline'} />
          <InfoIcon i18nKey={'text_disclaimer_patients_list'} />
        </Group>
      </Flex>
      <Box mt={'sm'}>
        <ActiveFiltersBar config={config} badges={useBadges()} datesKey={'last_measurement_label'} />
      </Box>
    </Box>
  );
};
